import { useState } from 'react';
import { ChevronDown, Dumbbell, Scale, TrendingDown, UtensilsCrossed } from 'lucide-react';
import type { Advice, AdviceType } from '@/engine/types';
import { useRewritten } from '@/features/llm/useRewritten';
import { cn } from '@/lib/utils';

const ICONS: Record<AdviceType, typeof UtensilsCrossed> = {
  cierre_dieta: UtensilsCrossed,
  progresion: Dumbbell,
  redundancia: Dumbbell,
  descompensacion: Dumbbell,
  historico: TrendingDown,
  tdee: Scale,
};

/**
 * Los avisos del motor.
 *
 * Tono neutro y sin culpabilizar, como pide el spec: se dice qué pasa y qué
 * hacer, nunca "has fallado". El porqué va plegado pero siempre disponible: un
 * consejo cuyo motivo no se puede comprobar es un consejo en el que no se
 * confía.
 */
export function AdviceList({
  advice,
  title = 'Sugerencias',
  rewrite = false,
}: {
  advice: readonly Advice[];
  title?: string;
  /** Reescribir los mensajes con la capa LLM, si el usuario la activó. */
  rewrite?: boolean;
}) {
  if (advice.length === 0) return null;
  const id = `avisos-${title.replace(/\s+/g, '-').toLowerCase()}`;

  return (
    <section aria-labelledby={id} className="flex flex-col gap-2">
      <h2 id={id} className="text-sm font-semibold">
        {title}
      </h2>
      <ul className="flex flex-col gap-2">
        {advice.map((item, index) => (
          <AdviceCard key={`${item.type}-${index}`} advice={item} rewrite={rewrite} />
        ))}
      </ul>
    </section>
  );
}

function AdviceCard({ advice, rewrite }: { advice: Advice; rewrite: boolean }) {
  const [open, setOpen] = useState(false);
  const Icon = ICONS[advice.type];
  const message = useRewritten(advice.message, rewrite);

  return (
    <li className="rounded-[var(--radius-card)] border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
      <div className="flex gap-3">
        <Icon
          aria-hidden="true"
          className="mt-0.5 size-5 shrink-0 text-[var(--color-brand-strong)]"
        />
        <div className="min-w-0 flex-1">
          <p className="text-sm">{message}</p>

          {advice.reason ? (
            <>
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                className="tap -ml-1 mt-1 flex items-center gap-1 rounded-lg px-1 text-xs font-medium text-[var(--color-text-muted)]"
              >
                Por qué
                <ChevronDown
                  aria-hidden="true"
                  className={cn('size-4 transition-transform', open && 'rotate-180')}
                />
              </button>
              {open ? (
                <p className="mt-1 text-xs text-[var(--color-text-muted)]">{advice.reason}</p>
              ) : null}
            </>
          ) : null}
        </div>
      </div>
    </li>
  );
}
